/**
 * 自助注册 (multi / saas 模式):
 *   手机号 + 验证码 必填; multi 模式且没有邀请码时必须填公司名 (本人成为公司管理员);
 *   带 ?invite=CODE 打开时自动填入邀请码并隐藏公司名 (加入邀请方公司, 角色为成员)。
 * 成功后后端直接返回登录结果。
 */
import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { UserPlus } from "lucide-react";
import { Alert, Button, EmptyState, Field, Input, Spinner, buttonVariants } from "@/components/ui";
import { useAuthStore } from "@/stores/auth-store";
import { errorDetail } from "@/lib/api-client";
import { usePublicConfig, useRegister } from "../hooks";
import { SmsCodeButton } from "../components/SmsCodeButton";
import { AuthCard, AuthShell } from "../components/AuthShell";
import { goLegacyHome, postLoginTarget } from "../utils";
import { LegacyHomeRedirect } from "@/components/layout/route-guards";

export function RegisterPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const config = usePublicConfig();
  const registerMutation = useRegister();

  const [invite, setInvite] = useState(() => params.get("invite")?.trim() ?? "");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  if (token && user) {
    const target = postLoginTarget(user.role);
    return target === null ? <LegacyHomeRedirect replace={false} /> : <Navigate to={target} replace />;
  }

  const mode = config.data?.deployment_mode;
  const hasInvite = invite.trim().length > 0;
  const needCompany = mode === "multi" && !hasInvite;
  const pending = registerMutation.isPending;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (!phone.trim() || !code.trim()) {
      setError("请输入手机号和验证码");
      return;
    }
    if (needCompany && !companyName.trim()) {
      setError("请填写公司名称");
      return;
    }
    if (password && password.length < 6) {
      setError("密码至少 6 位");
      return;
    }
    try {
      const data = await registerMutation.mutateAsync({
        phone: phone.trim(),
        code: code.trim(),
        invite_code: hasInvite ? invite.trim() : undefined,
        company_name: !hasInvite && companyName.trim() ? companyName.trim() : undefined,
        display_name: displayName.trim() || undefined,
        password: password || undefined,
        email: email.trim() || undefined,
      });
      // 普通用户 (受邀成员) 整页进旧版 /static/; 管理员 (新建公司者) 进仪表盘
      const target = postLoginTarget(data.user.role);
      if (target === null) goLegacyHome();
      else navigate(target, { replace: true });
    } catch (err) {
      setError(errorDetail(err, "注册失败"));
    }
  };

  if (config.isLoading) {
    return (
      <AuthShell>
        <div className="flex justify-center py-16">
          <Spinner className="h-6 w-6 text-primary" />
        </div>
      </AuthShell>
    );
  }

  if (!config.data?.register_enabled) {
    return (
      <AuthShell>
        <div className="rounded-2xl border bg-card shadow-lg shadow-black/[0.04]">
          <EmptyState
            icon={UserPlus}
            title={<h1 className="text-section">暂未开放注册</h1>}
            hint="请联系管理员为你开通账号。"
            action={
              <Link to="/login" className={buttonVariants({ variant: "outline" })}>
                返回登录
              </Link>
            }
          />
        </div>
      </AuthShell>
    );
  }

  const optional = <span className="ml-1 font-normal text-muted-foreground">(可选)</span>;

  return (
    <AuthShell>
      <AuthCard
        title="注册账号"
        subtitle={hasInvite ? "通过邀请加入公司" : mode === "multi" ? "创建你的公司，开始使用智能广告制作助手" : "手机号快速注册"}
      >
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <Field label="手机号">
            <Input id="phone" type="tel" autoComplete="tel" value={phone}
              onChange={(e) => setPhone(e.target.value)} placeholder="请输入手机号"
              className="h-10" disabled={pending} />
          </Field>

          <div className="space-y-1.5">
            <label htmlFor="code" className="text-sm font-medium text-foreground">验证码</label>
            <div className="flex gap-2">
              <Input id="code" type="text" inputMode="numeric" autoComplete="one-time-code"
                maxLength={6} value={code} onChange={(e) => setCode(e.target.value)}
                placeholder="6 位验证码" className="h-10 min-w-0 flex-1" disabled={pending} />
              <SmsCodeButton
                phone={phone}
                purpose="register"
                defaultInterval={config.data.sms_resend_interval_seconds ?? 60}
                disabled={pending}
                onError={setError}
                onSent={setInfo}
              />
            </div>
          </div>

          <Field label={<>邀请码{optional}</>}>
            <Input id="invite" type="text" value={invite}
              onChange={(e) => setInvite(e.target.value)} placeholder="有邀请码可填写"
              className="h-10" disabled={pending} />
          </Field>

          {needCompany && (
            <Field label="公司名称" required>
              <Input id="company" type="text" value={companyName}
                onChange={(e) => setCompanyName(e.target.value)} placeholder="请输入公司名称"
                className="h-10" disabled={pending} />
            </Field>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={<>姓名{optional}</>}>
              <Input id="display_name" type="text" autoComplete="name" value={displayName}
                onChange={(e) => setDisplayName(e.target.value)} placeholder="显示名称"
                className="h-10" disabled={pending} />
            </Field>
            <Field label={<>邮箱{optional}</>}>
              <Input id="email" type="email" autoComplete="email" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="用于接收通知"
                className="h-10" disabled={pending} />
            </Field>
          </div>

          <Field label={<>密码{optional}</>} hint="设置后也可用用户名 + 密码登录">
            <Input id="password" type="password" autoComplete="new-password" value={password}
              onChange={(e) => setPassword(e.target.value)} placeholder="至少 6 位"
              className="h-10" disabled={pending} />
          </Field>

          {info && !error && <Alert variant="success">{info}</Alert>}
          {error && <Alert>{error}</Alert>}

          <Button type="submit" size="lg" loading={pending} className="w-full">
            {pending ? "注册中..." : "注册"}
          </Button>

          <p className="text-center text-sm text-muted-foreground">
            已有账号？
            <Link to="/login" className="ml-1 font-medium text-primary hover:underline">登录</Link>
          </p>
        </form>
      </AuthCard>
    </AuthShell>
  );
}
